import { useState, useEffect } from "react";
import axios from "axios";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { X, BarChart3, Filter, RefreshCw, TrendingUp, Users, Euro } from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const COLORS = [
  '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', 
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'
];

function FilterSelect({ filterKey, filterData, currentValue, onChange }) {
  if (!filterData) return null;
  const options = filterData.values || [];
  const label = filterData.label || filterKey;
  
  return (
    <div>
      <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </label>
      <select
        value={currentValue || ""}
        onChange={(e) => onChange(filterKey, e.target.value)}
        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
      >
        <option value="">Tous</option>
        {options.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subValue, color }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-slate-400 text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
    </div>
  );
}

function CriteriaChart({ criteriaKey, criteriaData, viewMode }) {
  if (!criteriaData || !criteriaData.data || criteriaData.data.length === 0) {
    return null;
  }

  const chartData = criteriaData.data.map(item => ({
    name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
    fullName: item.name,
    Quantité: item.count,
    'CA (€)': item.ca,
    '% Quantité': item.count_pct,
    '% CA': item.ca_pct
  }));

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <h3 className="font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-purple-400" />
        {criteriaData.label}
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <div>
          <p className="text-xs text-slate-400 uppercase mb-2">
            {viewMode === 'quantity' ? 'Quantité' : 'Chiffre d\'Affaires (€)'}
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis type="number" stroke="#94a3b8" fontSize={11} />
              <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} width={80} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                formatter={(value, name) => {
                  if (name === 'CA (€)') return [`${value.toLocaleString('fr-FR')} €`, name];
                  return [value, name];
                }}
              />
              <Bar 
                dataKey={viewMode === 'quantity' ? 'Quantité' : 'CA (€)'} 
                fill={COLORS[0]} 
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div>
          <p className="text-xs text-slate-400 uppercase mb-2">Répartition (%)</p>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData.slice(0, 8)}
                dataKey={viewMode === 'quantity' ? '% Quantité' : '% CA'}
                nameKey="fullName"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name.substring(0, 10)}${name.length > 10 ? '...' : ''}`}
                labelLine={false}
                fontSize={10}
              >
                {chartData.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                formatter={(value) => [`${value}%`]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2 text-slate-400">Valeur</th>
              <th className="text-right py-2 text-slate-400">Qté</th>
              <th className="text-right py-2 text-slate-400">%</th>
              <th className="text-right py-2 text-slate-400">CA (€)</th>
              <th className="text-right py-2 text-slate-400">% CA</th>
            </tr>
          </thead>
          <tbody>
            {criteriaData.data.slice(0, 10).map((item, idx) => (
              <tr key={idx} className="border-b border-slate-700/50">
                <td className="py-2 text-white">{item.name}</td>
                <td className="py-2 text-right text-white">{item.count}</td>
                <td className="py-2 text-right text-purple-400">{item.count_pct}%</td>
                <td className="py-2 text-right text-white">{item.ca.toLocaleString('fr-FR')} €</td>
                <td className="py-2 text-right text-blue-400">{item.ca_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatsPage({ onBack, embedded = false }) {
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState('departement');
  const [viewMode, setViewMode] = useState('quantity'); // 'quantity' or 'ca'

  useEffect(() => {
    axios.get(`${API}/search/filters`)
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error("Error loading filters:", err));
    
    // Load initial stats
    loadStats({});
  }, []);

  const loadStats = (filtersToUse) => {
    setLoading(true);
    axios.post(`${API}/stats/advanced`, filtersToUse)
      .then(res => setStats(res.data))
      .catch(err => console.error("Error loading stats:", err))
      .finally(() => setLoading(false));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadStats(filters);
  };

  const handleReset = () => {
    setFilters({});
    loadStats({});
  };

  const filterKeyList = Object.keys(filterOptions);
  const activeCount = Object.keys(filters).length;

  const formatCA = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M €`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K €`;
    return `${value.toLocaleString('fr-FR')} €`;
  };

  return (
    <div className={embedded ? "text-white" : "min-h-screen bg-slate-900 text-white"}>
      {!embedded && (
      <header className="bg-slate-950 border-b border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-from-stats-btn"
              onClick={onBack}
              className="p-2 hover:bg-slate-800 rounded-lg"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Statistiques & Analyses</h1>
                <p className="text-xs text-slate-400">Comparaison par critères avec CA</p>
              </div>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('quantity')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'quantity' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Quantité
            </button>
            <button
              onClick={() => setViewMode('ca')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'ca' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              CA (€)
            </button>
          </div>
        </div>
      </header>
      )}
      
      {embedded && (
        <div className="flex items-center justify-end mb-4">
          <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('quantity')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'quantity' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Quantité
            </button>
            <button
              onClick={() => setViewMode('ca')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'ca' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              CA (€)
            </button>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Filters */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Filter className="w-5 h-5 text-purple-400" />
              Filtres
            </h2>
            {activeCount > 0 && (
              <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                {activeCount} filtre{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {filterKeyList.map((fk) => (
              <FilterSelect
                key={fk}
                filterKey={fk}
                filterData={filterOptions[fk]}
                currentValue={filters[fk]}
                onChange={handleFilterChange}
              />
            ))}
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-500 hover:to-blue-500 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              <span>Générer les statistiques</span>
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard
              icon={Users}
              label="Total Dossiers"
              value={stats.total_clients.toLocaleString('fr-FR')}
              subValue={activeCount > 0 ? "avec filtres appliqués" : "tous les dossiers"}
              color="bg-purple-600"
            />
            <StatCard
              icon={Euro}
              label="CA Total Devis HT"
              value={formatCA(stats.total_ca)}
              subValue={`${stats.total_ca.toLocaleString('fr-FR')} €`}
              color="bg-blue-600"
            />
            <StatCard
              icon={TrendingUp}
              label="CA Moyen / Dossier"
              value={stats.total_clients > 0 ? formatCA(stats.total_ca / stats.total_clients) : "0 €"}
              subValue={stats.total_clients > 0 ? `${Math.round(stats.total_ca / stats.total_clients).toLocaleString('fr-FR')} € par dossier` : ""}
              color="bg-green-600"
            />
          </div>
        )}

        {/* Criteria Selector */}
        {stats && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
              <span className="text-sm text-slate-400 whitespace-nowrap">Analyser par :</span>
              {filterKeyList.map((fk) => {
                const label = filterOptions[fk]?.label || fk;
                return (
                  <button
                    key={fk}
                    onClick={() => setSelectedCriteria(fk)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCriteria === fk 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Charts */}
        {stats && stats.by_criteria && (
          <CriteriaChart
            criteriaKey={selectedCriteria}
            criteriaData={stats.by_criteria[selectedCriteria]}
            viewMode={viewMode}
          />
        )}

        {/* All Criteria Overview */}
        {stats && stats.by_criteria && (
          <div className="mt-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Vue d'ensemble - Tous les critères
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filterKeyList.filter(fk => fk !== selectedCriteria).slice(0, 4).map((fk) => (
                <CriteriaChart
                  key={fk}
                  criteriaKey={fk}
                  criteriaData={stats.by_criteria[fk]}
                  viewMode={viewMode}
                />
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
            <p className="text-slate-400 mt-4">Chargement des statistiques...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default StatsPage;
